import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { University, Trip, BookingMode, TripDirection } from '../../types';
import { tripService, DateAvailabilityInfo } from '../../services/tripService';
import { bookingApi } from '../../services/bookingApi';
import { bookingService } from '../../services/bookingService';
import { referralService } from '../../services/referralService';
import { useAuth } from '../../auth/AuthContext';
import { BookingModeSelector, JourneyType } from './BookingModeSelector';
import { MultiDayCalendar } from '../calendar/MultiDayCalendar';
import { getSeatDefinition } from '../../constants/seatLayout';
import { useRouter } from '../../router/Router';
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  Bus,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  CreditCard,
  Building2,
  Smartphone,
  Check,
  Calendar,
  Repeat,
} from 'lucide-react';
import './MultiDayBookingWizard.css';

interface MultiDayBookingWizardProps {
  university: University;
  initialJourney?: JourneyType;
  onCancel: () => void;
}

interface PlannedTripSelection {
  date: string;
  direction: TripDirection;
  selectedTrip: Trip | null;
  selectedSeatNumber: number | null;
}

export const formatDisplayDate = (dateStr: string, options?: Intl.DateTimeFormatOptions): string => {
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return d.toLocaleDateString('ar-EG', options || {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }
  return dateStr;
};

export const MultiDayBookingWizard: React.FC<MultiDayBookingWizardProps> = ({
  university,
  initialJourney = 'ROUND_TRIP_SAME_DAY',
  onCancel,
}) => {
  const { navigate } = useRouter();
  const { user, studentProfile } = useAuth();

  // Wizard Step: 1 = Mode & Month Days, 2 = Departure & Return Times, 3 = Seats, 4 = Review & Pay
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);

  // 1. Journey Type: 'ONE_WAY' vs 'ROUND_TRIP_SAME_DAY'
  const [journeyType, setJourneyType] = useState<JourneyType>(initialJourney);

  // Availability maps
  const [outboundAvailability, setOutboundAvailability] = useState<Record<string, DateAvailabilityInfo>>({});
  const [returnAvailability, setReturnAvailability] = useState<Record<string, DateAvailabilityInfo>>({});
  const [allUniTrips, setAllUniTrips] = useState<Trip[]>([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);

  // 2. Selected Month Days
  const [selectedDates, setSelectedDates] = useState<string[]>([]);

  // 3. Planned items across dates & directions (Outbound and optional Return per date)
  const [plannedTrips, setPlannedTrips] = useState<PlannedTripSelection[]>([]);

  const [isLoadingSeats, setIsLoadingSeats] = useState(false);

  // Payment method selection
  const [paymentMethod, setPaymentMethod] = useState<'INSTAPAY' | 'WALLET' | 'BRANCH'>('INSTAPAY');

  // Submit & Error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 4. Track trips student has already booked to prevent double-booking on same trip
  const [activeBookedTripIds, setActiveBookedTripIds] = useState<Set<string>>(new Set());

  // Load student's existing active bookings to prevent duplicate bookings
  useEffect(() => {
    let isMounted = true;
    if (user?.uid) {
      bookingService.getStudentBookings(user.uid).then((bList) => {
        if (isMounted) {
          const bookedIds = new Set<string>();
          bList.forEach((b) => {
            if (
              b.tripId &&
              b.status !== 'CANCELLED' &&
              b.status !== 'DELETED' &&
              b.paymentStatus !== 'CANCELLED'
            ) {
              bookedIds.add(b.tripId);
            }
          });
          setActiveBookedTripIds(bookedIds);
        }
      }).catch(console.warn);
    }
    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  // Load Trips & Availability for University
  useEffect(() => {
    let isMounted = true;
    const loadAvailability = async () => {
      setIsLoadingAvailability(true);
      try {
        const trips = await tripService.getTripsForUniversity(university.id);
        const outMap = tripService.computeAvailabilityFromTrips(trips, 'OUTBOUND');
        const retMap = tripService.computeAvailabilityFromTrips(trips, 'RETURN');

        if (isMounted) {
          setOutboundAvailability(outMap);
          setReturnAvailability(retMap);
          setAllUniTrips(trips);

          const hasReturn = Object.keys(retMap).length > 0;
          const hasOutbound = Object.keys(outMap).length > 0;
          if (hasOutbound && !hasReturn) {
            setJourneyType('ONE_WAY');
          }
        }
      } catch (err) {
        console.error('Failed to load availability:', err);
      } finally {
        if (isMounted) setIsLoadingAvailability(false);
      }
    };

    loadAvailability();
    return () => {
      isMounted = false;
    };
  }, [university.id]);

  // Combined availability for ROUND_TRIP_SAME_DAY
  const combinedAvailability = useMemo(() => {
    if (journeyType === 'ONE_WAY') {
      return outboundAvailability;
    }

    const map: Record<string, DateAvailabilityInfo> = {};
    const allDates = new Set([
      ...Object.keys(outboundAvailability),
      ...Object.keys(returnAvailability),
    ]);

    for (const date of allDates) {
      const outInfo = outboundAvailability[date];
      const retInfo = returnAvailability[date];

      if (outInfo && retInfo && outInfo.hasAvailableSeats && retInfo.hasAvailableSeats) {
        map[date] = {
          date,
          tripCount: (outInfo.tripCount || 0) + (retInfo.tripCount || 0),
          availableSeatsCount: Math.min(outInfo.availableSeatsCount, retInfo.availableSeatsCount),
          hasAvailableSeats: true,
          minPrice: (outInfo.minPrice || 0) + (retInfo.minPrice || 0),
          status: 'AVAILABLE',
        };
      } else if (outInfo || retInfo) {
        const isFull = (outInfo?.status === 'FULL') || (retInfo?.status === 'FULL');
        map[date] = {
          date,
          tripCount: (outInfo?.tripCount || 0) + (retInfo?.tripCount || 0),
          availableSeatsCount: Math.min(outInfo?.availableSeatsCount || 0, retInfo?.availableSeatsCount || 0),
          hasAvailableSeats: false,
          minPrice: (outInfo?.minPrice || 0) + (retInfo?.minPrice || 0),
          status: isFull ? 'FULL' : 'NO_TRIP',
        };
      }
    }

    return map;
  }, [journeyType, outboundAvailability, returnAvailability]);

  // Handle Journey Mode change (ONE_WAY vs ROUND_TRIP_SAME_DAY)
  const handleJourneyChange = (newJourney: JourneyType) => {
    setJourneyType(newJourney);
    setErrorMessage(null);
  };

  // Helper to build or update planned trips list for Step 2
  const buildPlannedTrips = useCallback(() => {
    setPlannedTrips((prev) => {
      const list: PlannedTripSelection[] = [];
      const sorted = [...selectedDates].sort();

      for (const d of sorted) {
        // Outbound
        const availableOutTrips = allUniTrips.filter(
          (t) => t.date === d && (!t.direction || t.direction === 'OUTBOUND') && t.status === 'OPEN' && !activeBookedTripIds.has(t.id)
        );
        const existingOut = prev.find((p) => p.date === d && p.direction === 'OUTBOUND');
        const selectedOutTrip = existingOut?.selectedTrip || (availableOutTrips.length === 1 ? availableOutTrips[0] : null);
        list.push({
          date: d,
          direction: 'OUTBOUND',
          selectedTrip: selectedOutTrip,
          selectedSeatNumber: existingOut?.selectedSeatNumber || null,
        });

        // Return (if ROUND_TRIP_SAME_DAY)
        if (journeyType === 'ROUND_TRIP_SAME_DAY') {
          const availableRetTrips = allUniTrips.filter(
            (t) => t.date === d && t.direction === 'RETURN' && t.status === 'OPEN' && !activeBookedTripIds.has(t.id)
          );
          const existingRet = prev.find((p) => p.date === d && p.direction === 'RETURN');
          const selectedRetTrip = existingRet?.selectedTrip || (availableRetTrips.length === 1 ? availableRetTrips[0] : null);
          list.push({
            date: d,
            direction: 'RETURN',
            selectedTrip: selectedRetTrip,
            selectedSeatNumber: existingRet?.selectedSeatNumber || null,
          });
        }
      }

      return list;
    });
  }, [journeyType, selectedDates, allUniTrips, activeBookedTripIds]);

  // Advance from Step 1 to Step 2
  const handleProceedToTrips = () => {
    setErrorMessage(null);
    if (selectedDates.length === 0) {
      setErrorMessage('يرجى تحديد يوم واحد على الأقل من تقويم الشهر للمتابعة.');
      return;
    }

    buildPlannedTrips();
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Unique sorted dates in planned trips
  const plannedDatesList = useMemo(() => {
    return Array.from(new Set(selectedDates)).sort();
  }, [selectedDates]);

  // Check if all planned trips have a selected trip
  const allTripsSelected = useMemo(() => {
    return plannedTrips.length > 0 && plannedTrips.every((item) => item.selectedTrip !== null);
  }, [plannedTrips]);

  // Update a planned trip selection
  const handleSelectTripForPlanned = (index: number, trip: Trip) => {
    if (activeBookedTripIds.has(trip.id)) {
      setErrorMessage(`لديك حجز مسبق ومفعل على هذه الرحلة (${trip.date} - ${trip.departureTime}). لا يمكنك حجز نفس الرحلة مرتين.`);
      return;
    }
    setErrorMessage(null);
    setPlannedTrips((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;
      copy[index] = {
        ...copy[index],
        selectedTrip: trip,
        selectedSeatNumber: null,
      };
      return copy;
    });
  };

  // Auto-assign real sequential seats per trip from database using high-speed parallel batching
  const resolveSequentialSeats = useCallback(async (currentList: PlannedTripSelection[]) => {
    setIsLoadingSeats(true);
    try {
      const tripIds = currentList.map((item) => item.selectedTrip?.id).filter(Boolean) as string[];
      // Batch fetch all occupied seats in ONE PARALLEL CALL!
      const occupiedMap = await tripService.getOccupiedSeatsForMultipleTrips(tripIds);

      const claimedByTrip: Record<string, Set<number>> = {};
      const updated = [...currentList];

      for (let i = 0; i < updated.length; i++) {
        const item = updated[i];
        if (item.selectedTrip) {
          const tripId = item.selectedTrip.id;
          const occupied = new Set(occupiedMap[tripId] || []);
          if (!claimedByTrip[tripId]) {
            claimedByTrip[tripId] = new Set<number>();
          }
          const claimed = claimedByTrip[tripId];
          const capacity = item.selectedTrip.capacity || 50;

          let chosenSeat = capacity;
          for (let seatNum = 1; seatNum <= capacity; seatNum++) {
            if (!occupied.has(seatNum) && !claimed.has(seatNum)) {
              chosenSeat = seatNum;
              break;
            }
          }

          claimed.add(chosenSeat);
          updated[i] = {
            ...item,
            selectedSeatNumber: chosenSeat,
          };
        }
      }

      setPlannedTrips(updated);
    } catch (err) {
      console.warn('Failed to resolve sequential seats:', err);
    } finally {
      setIsLoadingSeats(false);
    }
  }, []);

  // Auto-assign sequential seats for all planned trips upon entering Step 3
  useEffect(() => {
    if (currentStep === 3) {
      resolveSequentialSeats(plannedTrips);
    }
  }, [currentStep]);

  // Check if all seats have been selected
  const allSeatsSelected = useMemo(() => {
    return plannedTrips.length > 0 && plannedTrips.every((item) => item.selectedSeatNumber !== null);
  }, [plannedTrips]);

  // Total price
  const totalAmount = useMemo(() => {
    return plannedTrips.reduce((sum, item) => sum + (item.selectedTrip?.price || 0), 0);
  }, [plannedTrips]);

  // Compute backend bookingMode
  const computedBookingMode: BookingMode = useMemo(() => {
    if (journeyType === 'ROUND_TRIP_SAME_DAY') {
      return 'ROUND_TRIP';
    }
    return selectedDates.length > 1 ? 'MULTI_DAY' : 'SINGLE';
  }, [journeyType, selectedDates]);

  // Handle Final Submission (Step 4 -> reserveMultiSeats)
  const handleConfirmReservation = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const items = plannedTrips.map((item) => ({
        tripId: item.selectedTrip!.id,
        seatNumber: item.selectedSeatNumber || 1,
        direction: item.direction,
        trip: item.selectedTrip!,
      }));

      // 1. Acquire seat locks if backend requires active locks
      try {
        await bookingApi.requestMultiSeatHold({
          items: items.map((it) => ({ tripId: it.tripId, seatNumber: it.seatNumber })),
        });
      } catch (holdErr) {
        console.warn('Pre-hold acquired or skipped:', holdErr);
      }

      // 2. Reserve seats and create booking group atomically
      const res = await bookingApi.reserveMultiSeats({
        items,
        bookingMode: computedBookingMode,
        paymentMethod,
        studentName: studentProfile?.fullName,
        studentPhone: studentProfile?.phoneNumber,
      });

      if (res.success && res.data) {
        if (user?.uid) {
          referralService.recordBookingForReferral(user.uid, res.data.bookingGroupId).catch(console.warn);
        }
        navigate(`/student/payment/${res.data.bookingGroupId}`);
      } else {
        setErrorMessage(res.error?.messageAr || 'تعذر تأكيد الحجز. يرجى مراجعة المقاعد والمحاولة مرة أخرى.');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'حدث خطأ غير متوقع أثناء إتمام الحجز.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="multi-day-wizard-container" dir="rtl">
      {/* Wizard Header Stepper */}
      <div className="wizard-stepper-bar">
        <div className={`wizard-step-node ${currentStep >= 1 ? 'active' : ''}`}>
          <span className="step-num">1</span>
          <span className="step-name">نوع الرحلة والأيام</span>
        </div>
        <div className={`wizard-step-line ${currentStep >= 2 ? 'filled' : ''}`}></div>
        <div className={`wizard-step-node ${currentStep >= 2 ? 'active' : ''}`}>
          <span className="step-num">2</span>
          <span className="step-name">مواعيد الرحلات</span>
        </div>
        <div className={`wizard-step-line ${currentStep >= 3 ? 'filled' : ''}`}></div>
        <div className={`wizard-step-node ${currentStep >= 3 ? 'active' : ''}`}>
          <span className="step-num">3</span>
          <span className="step-name">المقاعد</span>
        </div>
        <div className={`wizard-step-line ${currentStep >= 4 ? 'filled' : ''}`}></div>
        <div className={`wizard-step-node ${currentStep >= 4 ? 'active' : ''}`}>
          <span className="step-num">4</span>
          <span className="step-name">التأكيد والسداد</span>
        </div>
      </div>

      {/* University Title Badge */}
      <div className="wizard-uni-badge-row">
        <div className="uni-badge-info">
          <Bus size={18} style={{ color: '#E5232A' }} />
          <strong>{university.name}</strong>
          <span className="text-xs text-slate-500">({university.governorate})</span>
        </div>
        <button type="button" className="btn-wizard-cancel" onClick={onCancel}>
          تغيير الجامعة
        </button>
      </div>

      {/* Student Assigned Route & Launch Point Banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '10px 16px',
          backgroundColor: '#eff6ff',
          borderRadius: '12px',
          border: '1px solid #bfdbfe',
          marginBottom: '1rem',
          fontSize: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e3a8a' }}>
          <Bus size={16} style={{ color: '#2563eb' }} />
          <span>
            خط سيرك المعتمد أثناء الحجز:{' '}
            <strong style={{ color: '#1d4ed8' }}>
              {studentProfile?.assignedRoute || `الموقف الجديد بمحرم بيك ◄ ${university.name}`}
            </strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569', fontSize: '11px' }}>
          <span>محطة الانطلاق:</span>
          <strong style={{ color: '#0f172a' }}>
            {studentProfile?.defaultPickupStation || 'الموقف الجديد بمحرم بيك'}
          </strong>
        </div>
      </div>

      {/* Error Alert */}
      {errorMessage && (
        <div className="auth-error-banner" role="alert" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={20} className="auth-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* =========================================================================
          STEP 1: 1. JOURNEY TYPE (ذهاب فقط أم ذهاب وعودة) + 2. MONTH DAYS
          ========================================================================= */}
      {currentStep === 1 && (
        <div className="wizard-step-content step-1">
          {/* 1. Journey Type Selector */}
          <BookingModeSelector
            selectedJourney={journeyType}
            onChange={handleJourneyChange}
          />

          {/* 2. Month Calendar Selection */}
          <div className="calendar-step-section">
            <div className="calendar-section-header">
              <div className="section-title-wrap">
                <Calendar style={{ color: '#E5232A' }} size={20} />
                <span className="text-sm font-bold text-slate-800">2. حدد أيام السفر من هذا الشهر:</span>
              </div>
              <span className="text-xs text-slate-500">
                {journeyType === 'ROUND_TRIP_SAME_DAY'
                  ? 'اضغط على أيام محاضراتك لحجز ذهاب وعودة في نفس اليوم'
                  : 'اضغط على أيام محاضراتك لحجز رحلات الذهاب فقط'}
              </span>
            </div>

            {isLoadingAvailability ? (
              <div className="loading-state-card" style={{ padding: '3rem 1rem' }}>
                <div className="loading-spinner"></div>
                <p>جاري فحص جدول الرحلات والمقاعد المتاحة بالتقويم...</p>
              </div>
            ) : (
              <div className="multi-day-dates-flow">
                <MultiDayCalendar
                  availabilityMap={combinedAvailability}
                  selectedDates={selectedDates}
                  onSelectDates={setSelectedDates}
                  directionLabel={
                    journeyType === 'ROUND_TRIP_SAME_DAY'
                      ? 'مواعيد ذهاب وعودة في نفس اليوم'
                      : 'مواعيد الذهاب من الإسكندرية'
                  }
                />

                <div className="step-1-actions">
                  <button
                    type="button"
                    className="btn-wizard-next"
                    disabled={selectedDates.length === 0}
                    onClick={handleProceedToTrips}
                  >
                    <span>
                      متابعة لاختيار مواعيد {journeyType === 'ROUND_TRIP_SAME_DAY' ? 'الذهاب والعودة' : 'الذهاب'} (
                      {selectedDates.length} {selectedDates.length === 1 ? 'يوم' : 'أيام مختارة'})
                    </span>
                    <ArrowLeft size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================================
          STEP 2: OUTBOUND & RETURN TIMES PER DAY
          ========================================================================= */}
      {currentStep === 2 && (
        <div className="wizard-step-content step-2">
          <div className="step-header">
            <h3>مواعيد الذهاب {journeyType === 'ROUND_TRIP_SAME_DAY' ? 'والعودة' : ''} لـ {university.name}</h3>
            <p>
              اختر الموعد الأنسب لجدول محاضراتك لكل يوم من الأيام التي حددتها.
            </p>
          </div>

          <div className="planned-trips-list">
            {plannedDatesList.map((dateStr, dateIdx) => {
              const formattedDate = formatDisplayDate(dateStr);

              // Outbound item & matching trips
              const outboundIndex = plannedTrips.findIndex(
                (p) => p.date === dateStr && p.direction === 'OUTBOUND'
              );
              const outboundItem = outboundIndex !== -1 ? plannedTrips[outboundIndex] : null;
              const outboundTrips = allUniTrips.filter(
                (t) =>
                  t.date === dateStr &&
                  (!t.direction || t.direction === 'OUTBOUND') &&
                  t.status === 'OPEN'
              );

              // Return item & matching trips (if ROUND_TRIP_SAME_DAY)
              const returnIndex = plannedTrips.findIndex(
                (p) => p.date === dateStr && p.direction === 'RETURN'
              );
              const returnItem = returnIndex !== -1 ? plannedTrips[returnIndex] : null;
              const returnTrips = allUniTrips.filter(
                (t) =>
                  t.date === dateStr &&
                  t.direction === 'RETURN' &&
                  t.status === 'OPEN'
              );

              return (
                <div key={dateStr} className="day-schedule-card">
                  {/* Day Header */}
                  <div className="day-schedule-header">
                    <div className="day-title-box">
                      <Calendar size={18} style={{ color: '#E5232A' }} />
                      <strong className="day-title-text">{formattedDate}</strong>
                    </div>
                    <span className="day-counter-pill">
                      اليوم {dateIdx + 1} من {plannedDatesList.length}
                    </span>
                  </div>

                  {/* 1. Outbound Time Section */}
                  <div className="direction-schedule-box outbound">
                    <div className="direction-box-header">
                      <div className="dir-icon-title">
                        <ArrowRight size={16} style={{ color: '#E5232A' }} />
                        <span className="dir-label">
                          <strong>موعد رحلة الذهاب:</strong> الإسكندرية ◄ {university.name}
                        </span>
                      </div>
                      {outboundItem?.selectedTrip && (
                        <span className="selected-time-badge">
                          <Check size={14} /> تم اختيار موعد {outboundItem.selectedTrip.departureTime} ص
                        </span>
                      )}
                    </div>

                    {outboundTrips.length === 0 ? (
                      <div className="no-trips-day-alert">
                        <AlertCircle size={16} className="text-amber-500" />
                        <span>لا توجد رحلات ذهاب مجدولة مفتوحة لهذا اليوم.</span>
                      </div>
                    ) : (
                      <div className="available-trips-options">
                        {outboundTrips.map((trip) => {
                          const isChosen = outboundItem?.selectedTrip?.id === trip.id;
                          const isAlreadyBooked = activeBookedTripIds.has(trip.id);
                          return (
                            <div
                              key={trip.id}
                              className={`trip-option-row ${isChosen ? 'chosen' : ''} ${isAlreadyBooked ? 'opacity-60 bg-amber-50/40' : ''}`}
                              onClick={() => {
                                if (!isAlreadyBooked) {
                                  handleSelectTripForPlanned(outboundIndex, trip);
                                }
                              }}
                            >
                              <div className="trip-time-col">
                                <Clock size={16} style={{ color: '#E5232A' }} />
                                <strong className="font-mono text-base">{trip.departureTime} ص</strong>
                                <span className="text-xs text-slate-500">
                                  وصول متوقع {trip.expectedArrivalTime || '—'}
                                </span>
                              </div>

                              <div className="trip-veh-col">
                                <span className="veh-name">
                                  {(trip.capacity || 50) <= 16
                                    ? 'ميكروباص / هاي اس'
                                    : (trip.capacity || 50) <= 32
                                    ? 'ميني باص'
                                    : 'حافلة'}{' '}
                                  ({trip.capacity || trip.totalSeats || 50} راكباً)
                                </span>
                                <span className="avail-seats">
                                  {isAlreadyBooked ? (
                                    <span className="text-xs text-amber-700 font-bold bg-amber-100 px-1.5 py-0.5 rounded">لديك حجز مسبق ✅</span>
                                  ) : (
                                    <>
                                      <strong>{trip.availableSeats}</strong> مقاعد متاحة
                                    </>
                                  )}
                                </span>
                              </div>

                              <div className="trip-price-col">
                                <span className="price-tag font-bold">{trip.price} ج.م</span>
                                <button
                                  type="button"
                                  disabled={isAlreadyBooked}
                                  className={`btn-choose-trip ${isAlreadyBooked ? 'cursor-not-allowed opacity-60' : isChosen ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isAlreadyBooked) {
                                      handleSelectTripForPlanned(outboundIndex, trip);
                                    }
                                  }}
                                >
                                  {isAlreadyBooked ? 'محجوزة مسبقاً' : isChosen ? 'محدد ✓' : 'اختيار'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 2. Return Time Section (if ROUND_TRIP_SAME_DAY) */}
                  {journeyType === 'ROUND_TRIP_SAME_DAY' && (
                    <div className="direction-schedule-box return">
                      <div className="direction-box-header">
                        <div className="dir-icon-title">
                          <Repeat size={16} className="text-emerald-600" />
                          <span className="dir-label">
                            <strong>موعد رحلة العودة (نفس اليوم):</strong> {university.name} ◄ الإسكندرية
                          </span>
                        </div>
                        {returnItem?.selectedTrip && (
                          <span className="selected-time-badge return">
                            <Check size={14} /> تم اختيار موعد {returnItem.selectedTrip.departureTime}
                          </span>
                        )}
                      </div>

                      {returnTrips.length === 0 ? (
                        <div className="no-trips-day-alert">
                          <AlertCircle size={16} className="text-amber-500" />
                          <span>لا توجد رحلات عودة مجدولة مفتوحة لهذا اليوم.</span>
                        </div>
                      ) : (
                        <div className="available-trips-options">
                          {returnTrips.map((trip) => {
                            const isChosen = returnItem?.selectedTrip?.id === trip.id;
                            const isAlreadyBooked = activeBookedTripIds.has(trip.id);
                            return (
                              <div
                                key={trip.id}
                                className={`trip-option-row return-option ${isChosen ? 'chosen' : ''} ${isAlreadyBooked ? 'opacity-60 bg-amber-50/40' : ''}`}
                                onClick={() => {
                                  if (!isAlreadyBooked) {
                                    handleSelectTripForPlanned(returnIndex, trip);
                                  }
                                }}
                              >
                                <div className="trip-time-col">
                                  <Clock size={16} className="text-emerald-600" />
                                  <strong className="font-mono text-base">{trip.departureTime}</strong>
                                  <span className="text-xs text-slate-500">
                                    وصول متوقع {trip.expectedArrivalTime || '—'}
                                  </span>
                                </div>

                                <div className="trip-veh-col">
                                  <span className="veh-name">
                                    {(trip.capacity || 50) <= 16
                                      ? 'ميكروباص / هاي اس'
                                      : (trip.capacity || 50) <= 32
                                      ? 'ميني باص'
                                      : 'حافلة'}{' '}
                                    ({trip.capacity || trip.totalSeats || 50} راكباً)
                                  </span>
                                  <span className="avail-seats">
                                    {isAlreadyBooked ? (
                                      <span className="text-xs text-amber-700 font-bold bg-amber-100 px-1.5 py-0.5 rounded">لديك حجز مسبق ✅</span>
                                    ) : (
                                      <>
                                        <strong>{trip.availableSeats}</strong> مقاعد متاحة
                                      </>
                                    )}
                                  </span>
                                </div>

                                <div className="trip-price-col">
                                  <span className="price-tag font-bold">{trip.price} ج.م</span>
                                  <button
                                    type="button"
                                    disabled={isAlreadyBooked}
                                    className={`btn-choose-trip return-btn ${isAlreadyBooked ? 'cursor-not-allowed opacity-60' : isChosen ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isAlreadyBooked) {
                                        handleSelectTripForPlanned(returnIndex, trip);
                                      }
                                    }}
                                  >
                                    {isAlreadyBooked ? 'محجوزة مسبقاً' : isChosen ? 'محدد ✓' : 'اختيار'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="step-nav-bar">
            <button
              type="button"
              className="btn-wizard-back"
              onClick={() => setCurrentStep(1)}
            >
              <ArrowRight size={16} />
              <span>العودة للتقويم والأيام</span>
            </button>

            <button
              type="button"
              className="btn-wizard-next"
              disabled={!allTripsSelected}
              onClick={() => {
                setCurrentStep(3);
                resolveSequentialSeats(plannedTrips);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span>
                متابعة لتأكيد المقاعد المعينة بالترتيب ({plannedTrips.filter((p) => p.selectedTrip !== null).length}/{plannedTrips.length})
              </span>
              <ArrowLeft size={16} />
            </button>
          </div>
        </div>
      )}

      {/* =========================================================================
          STEP 3: AUTO-ASSIGNED SEQUENTIAL SEATS SUMMARY PER TRIP
          ========================================================================= */}
      {currentStep === 3 && (
        <div className="wizard-step-content step-3">
          <div className="step-header">
            <h3>المقاعد المخصصة تلقائياً لرحلاتك حسب سعة المركبة</h3>
            <p>يقوم النظام بتعيين المقاعد آلياً بالترتيب وفقاً لأسبقية الحجز لضمان انسيابية وراحة صعود الركاب.</p>
          </div>

          {/* Automatic Assignment Explanation Banner */}
          <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border border-blue-200 text-slate-800 mb-4 flex items-start gap-3 shadow-sm">
            <div className="p-2 rounded-xl bg-blue-600 text-white flex-shrink-0 mt-0.5">
              <Bus size={20} />
            </div>
            <div>
              <strong className="text-sm text-blue-900 block mb-0.5">
                نظام التعيين الآلي للمقاعد بالترتيب
              </strong>
              <p className="text-xs text-slate-600 leading-relaxed m-0">
                تم تخصيص المقاعد الموضحة أدناه آلياً بالترتيب التصاعدي (1، 2، 3 ...) وفقاً لأسبقية وتوقيت الحجز، مما يوفر عليك عناء الاختيار اليدوي ويضمن حجزاً منظماً ومؤكداً باسمك.
              </p>
            </div>
          </div>

          {isLoadingSeats ? (
            <div className="loading-state-card" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
              <div className="loading-spinner"></div>
              <p>جاري تعيين المقاعد تلقائياً لكافة رحلاتك بالترتيب...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {plannedTrips.map((item, idx) => {
                const dayStr = formatDisplayDate(item.date, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                });
                const seatDef = item.selectedSeatNumber ? getSeatDefinition(item.selectedSeatNumber) : null;

                return (
                  <div
                    key={idx}
                    className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all hover:border-blue-300"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm flex-shrink-0 border border-blue-100">
                        {idx + 1}
                      </div>

                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <strong className="text-sm text-slate-900 font-bold">{dayStr}</strong>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                              item.direction === 'RETURN'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}
                          >
                            {item.direction === 'RETURN' ? 'رحلة العودة' : 'رحلة الذهاب'}
                          </span>
                        </div>

                        <div className="text-xs text-slate-500 space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <Clock size={12} className="text-slate-400" />
                            <span>
                              موعد الإقلاع: <strong>{item.selectedTrip?.departureTime}</strong> | وصول متوقع: {item.selectedTrip?.expectedArrivalTime || '—'}
                            </span>
                          </div>
                          <div>
                            المسار:{' '}
                            <span className="text-slate-700 font-medium">
                              {item.selectedTrip?.origin} ◄ {item.selectedTrip?.destination || university.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Auto-Assigned Seat Badge */}
                    <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                      <div className="text-right">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 text-blue-800 border-2 border-blue-200">
                          <CheckCircle2 size={16} className="text-emerald-600" />
                          <strong className="font-mono text-sm font-extrabold">
                            المقعد #{item.selectedSeatNumber || '1'}
                          </strong>
                          <span className="text-[11px] text-blue-600">
                            (تلقائي بالترتيب)
                          </span>
                        </div>
                        {seatDef && (
                          <span className="text-[11px] text-slate-400 block mt-0.5">
                            {seatDef.labelAr}
                          </span>
                        )}
                      </div>

                      <div className="font-mono text-sm font-bold text-slate-800 text-left">
                        {item.selectedTrip?.price} ج.م
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Stepper Navigation */}
          <div className="step-nav-bar">
            <button
              type="button"
              className="btn-wizard-back"
              onClick={() => setCurrentStep(2)}
            >
              <ArrowRight size={16} />
              <span>العودة لاختيار المواعيد</span>
            </button>

            <button
              type="button"
              className="btn-wizard-next"
              disabled={!allSeatsSelected || isLoadingSeats}
              onClick={() => {
                setCurrentStep(4);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span>
                متابعة لمراجعة الحجز والسداد (إجمالي {totalAmount} ج.م)
              </span>
              <ArrowLeft size={16} />
            </button>
          </div>
        </div>
      )}

      {/* =========================================================================
          STEP 4: GROUP REVIEW & PAYMENT METHOD
          ========================================================================= */}
      {currentStep === 4 && (
        <div className="wizard-step-content step-4">
          <div className="step-header">
            <h3>ملخص رحلاتك وسداد الحجز</h3>
            <p>راجع مواعيد الرحلات والمقاعد المحددة قبل الانتقال إلى بوابة السداد.</p>
          </div>

          <div className="group-summary-card">
            <div className="group-summary-header">
              <div className="header-titles">
                <span className="group-badge-title">
                  {journeyType === 'ROUND_TRIP_SAME_DAY'
                    ? 'حجز ذهاب وعودة في نفس اليوم'
                    : 'حجز رحلات ذهاب فقط'}
                </span>
                <h4>{university.name}</h4>
              </div>
              <div className="total-amount-box">
                <span className="amount-label">إجمالي المبلغ المطلوب:</span>
                <strong className="amount-val font-mono">{totalAmount} ج.م</strong>
                <span className="trips-count-badge">{plannedTrips.length} رحلات</span>
              </div>
            </div>

            <div className="group-items-breakdown">
              {plannedTrips.map((item, idx) => {
                const dayLabel = formatDisplayDate(item.date, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                });

                return (
                  <div key={idx} className="group-item-row">
                    <div className="item-left-col">
                      <span className="item-index font-mono">#{idx + 1}</span>
                      <div className="item-text">
                        <strong className="item-date">{dayLabel}</strong>
                        <span className="item-route">
                          {item.direction === 'RETURN'
                            ? `عودة: ${university.name} ◄ الإسكندرية (إقلاع ${item.selectedTrip?.departureTime})`
                            : `ذهاب: الإسكندرية ◄ ${university.name} (إقلاع ${item.selectedTrip?.departureTime} ص)`}
                        </span>
                      </div>
                    </div>

                    <div className="item-right-col">
                      <span className="item-seat-badge">مقعد رقم {item.selectedSeatNumber}</span>
                      <strong className="item-price font-mono">{item.selectedTrip?.price} ج.م</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Method Selector */}
          <div className="payment-method-selector-card">
            <h4 className="payment-method-heading">طريقة السداد المفضلة:</h4>
            <div className="payment-methods-grid">
              <label
                className={`pm-card ${paymentMethod === 'INSTAPAY' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('INSTAPAY')}
              >
                <input
                  type="radio"
                  name="pm"
                  checked={paymentMethod === 'INSTAPAY'}
                  onChange={() => setPaymentMethod('INSTAPAY')}
                />
                <div className="pm-icon-wrap">
                  <Smartphone size={22} className="text-purple-600" />
                </div>
                <div className="pm-info">
                  <strong>تطبيق إنستاباي (InstaPay)</strong>
                  <span>تحويل لحظي مباشر لحساب OTO المعتمد</span>
                </div>
              </label>

              <label
                className={`pm-card ${paymentMethod === 'WALLET' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('WALLET')}
              >
                <input
                  type="radio"
                  name="pm"
                  checked={paymentMethod === 'WALLET'}
                  onChange={() => setPaymentMethod('WALLET')}
                />
                <div className="pm-icon-wrap">
                  <CreditCard size={22} className="text-emerald-600" />
                </div>
                <div className="pm-info">
                  <strong>محفظة إلكترونية (Mobile Wallet)</strong>
                  <span>فودافون كاش، أورنج كاش، اتصالات كاش، وي باي</span>
                </div>
              </label>

              <label
                className={`pm-card ${paymentMethod === 'BRANCH' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('BRANCH')}
              >
                <input
                  type="radio"
                  name="pm"
                  checked={paymentMethod === 'BRANCH'}
                  onChange={() => setPaymentMethod('BRANCH')}
                />
                <div className="pm-icon-wrap">
                  <Building2 size={22} className="text-[#E5232A]" />
                </div>
                <div className="pm-info">
                  <strong>الدفع في الفرع خلال 24 ساعة</strong>
                  <span>سداد نقدًا بمقر OTO (سيدي جابر / محطة مصر)</span>
                </div>
              </label>
            </div>
          </div>

          {/* Navigation & Submission */}
          <div className="step-nav-bar">
            <button
              type="button"
              className="btn-wizard-back"
              disabled={isSubmitting}
              onClick={() => setCurrentStep(3)}
            >
              <ArrowRight size={16} />
              <span>العودة لاختيار المقاعد</span>
            </button>

            <button
              type="button"
              className="btn-wizard-confirm"
              disabled={isSubmitting}
              onClick={handleConfirmReservation}
            >
              {isSubmitting ? (
                <>
                  <div className="btn-spinner"></div>
                  <span>جاري حجز مقاعد المجموعة وتأكيد الطلب...</span>
                </>
              ) : (
                <>
                  <ShieldCheck size={20} />
                  <span>تأكيد الحجز ومتابعة السداد ({totalAmount} ج.م)</span>
                  <ArrowLeft size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
